import { FunctionComponent, SVGProps, useState } from "react";
import styles from "./UtilityFeatureConfiguration.module.scss";
import ToggleSlider from "../../../../../common/components/xepp/toggle-slider/ToggleSlider";
import { BpsSegmentedControl } from "../../../../../common/components/bps-admin/segmented-control/BpsSegmentedControl";
import CommonLoadingSpinner from "../../../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import EmptyState from "../../../../../common/components/xepp/empty-state/EmptyState";
import {
	AccessAccountFFIcon,
	BillingRecordsFFIcon,
	BillsPaymentFFIcon,
	BuyingAndSharingPowerFFIcon,
	EnergyServiceFFIcon,
	IntegrationSecretKeysFFIcon,
	PaymentFFIcon,
	ServiceRequestFFIcon,
	SolarMarketplaceFFIcon,
	SupportDeskFFIcon,
} from "../../../../../assets/icons";
import { FeatureUserType, UtilityFeatureGroup } from "../../../../../model/bps-admin/utility-configuration";
import { useGetUtilityFeatureGroupsQuery } from "../../../../../service/utility-configuration-service";

// Which portal a group belongs to: the customer app is "Mobile", the utility admin
// portal is "Admin". Mobile first, because it holds most of the catalogue.
const AUDIENCES = [
	{ id: "admin", label: "Admin", userType: FeatureUserType.Utility },
	{ id: "customer", label: "Customer", userType: FeatureUserType.Customer },
];

/**
 * Icon per group, keyed by the seeded group id — group names arrive already
 * translated, so the id is the only stable handle. A group seeded later simply
 * renders without an icon.
 */
const GROUP_ICONS: Record<string, FunctionComponent<SVGProps<SVGSVGElement>>> = {
	"10000000-0000-0000-0000-000000000001": AccessAccountFFIcon, // Access & Account
	"10000000-0000-0000-0000-000000000002": BuyingAndSharingPowerFFIcon, // Buying & Sharing Power
	"10000000-0000-0000-0000-000000000003": PaymentFFIcon, // Payment
	"10000000-0000-0000-0000-000000000004": EnergyServiceFFIcon, // Energy & Service
	"10000000-0000-0000-0000-000000000005": SolarMarketplaceFFIcon, // Solar Marketplace
	"10000000-0000-0000-0000-000000000006": BillsPaymentFFIcon, // Bills & Payment Operations
	"10000000-0000-0000-0000-000000000007": ServiceRequestFFIcon, // Service Request
	"10000000-0000-0000-0000-000000000008": SupportDeskFFIcon, // Support Desk
	"10000000-0000-0000-0000-000000000009": IntegrationSecretKeysFFIcon, // Roles Management
	"10000000-0000-0000-0000-000000000010": BillingRecordsFFIcon, // Billing & Records
};

export interface UtilityFeatureFlags {
	customerFeatureFlags: string[];
	adminFeatureFlags: string[];
}

interface UtilityFeatureConfigurationProps {
	value: UtilityFeatureFlags;
	onChange: (value: UtilityFeatureFlags) => void;
}

/**
 * The feature catalogue, grouped, with a toggle per feature. The list is fetched when
 * the tab is opened — this component only mounts then.
 *
 * What is switched on is held by the page and saved with the rest of the form, which is
 * also why it survives a tab switch that unmounts this component. Flags are tracked by
 * Flagsmith code, because that is what the save endpoint takes.
 */
export function UtilityFeatureConfiguration({ value, onChange }: UtilityFeatureConfigurationProps) {
	const [audienceId, setAudienceId] = useState(AUDIENCES[1].id);

	const { data: groups = [], isFetching } = useGetUtilityFeatureGroupsQuery();

	const audience = AUDIENCES.find(item => item.id === audienceId) ?? AUDIENCES[0];
	const visibleGroups = groups.filter(group => group.userType === audience.userType);

	// Only ever the list of the audience on screen: a group belongs to one portal, so a
	// toggle can never touch the other list.
	const isCustomer = audience.userType === FeatureUserType.Customer;
	const enabledCodes = isCustomer ? value.customerFeatureFlags : value.adminFeatureFlags;

	const applyCodes = (codes: string[]) =>
		onChange(
			isCustomer ? { ...value, customerFeatureFlags: codes } : { ...value, adminFeatureFlags: codes },
		);

	const toggleFeature = (code: string, isOn: boolean) =>
		applyCodes(isOn ? [...enabledCodes, code] : enabledCodes.filter(item => item !== code));

	const toggleGroup = (group: UtilityFeatureGroup, isOn: boolean) => {
		const groupCodes = group.features.map(feature => feature.flagsMithCode);
		const withoutGroup = enabledCodes.filter(code => !groupCodes.includes(code));

		applyCodes(isOn ? [...withoutGroup, ...groupCodes] : withoutGroup);
	};

	return (
		<CommonLoadingSpinner loading={isFetching} color="var(--app-primary)">
			<div className={styles.container}>
				<BpsSegmentedControl
					segments={AUDIENCES}
					activeSegmentId={audienceId}
					onSegmentChange={setAudienceId}
				/>

				{visibleGroups.length === 0 ? (
					<EmptyState
						title="No features found"
						description="Features available for this portal will show up here."
					/>
				) : (
					<div className={styles.groupGrid}>
						{visibleGroups.map(group => {
							const GroupIcon = GROUP_ICONS[group.id];
							const onCount = group.features.filter(feature =>
								enabledCodes.includes(feature.flagsMithCode),
							).length;

							return (
								<section key={group.id} className={styles.group}>
									<header className={styles.groupHeader}>
										{GroupIcon && (
											<span className={styles.groupIcon}>
												<GroupIcon />
											</span>
										)}
										<div className={styles.groupTitleGroup}>
											<h4 className={styles.groupName}>{group.name}</h4>
											<p className={styles.groupMeta}>
												{group.description} · {onCount} of {group.features.length} on
											</p>
										</div>
										<span className={styles.toggleAllLabel}>Toggle All</span>
										<ToggleSlider
											size="sm"
											isActive={group.features.length > 0 && onCount === group.features.length}
											onChange={isOn => toggleGroup(group, isOn)}
										/>
									</header>

									<ul className={styles.featureList}>
										{group.features.map(feature => (
											<li key={feature.id} className={styles.feature}>
												<div className={styles.featureText}>
													<span className={styles.featureName}>{feature.name}</span>
													<span className={styles.featureDescription}>
														{feature.description}
													</span>
												</div>
												<ToggleSlider
													size="sm"
													isActive={enabledCodes.includes(feature.flagsMithCode)}
													onChange={isOn => toggleFeature(feature.flagsMithCode, isOn)}
												/>
											</li>
										))}
									</ul>
								</section>
							);
						})}
					</div>
				)}
			</div>
		</CommonLoadingSpinner>
	);
}
